import {
  SegmentAIConditionRequest,
  SegmentAIConditionResponse,
  SegmentAIProfileRequest,
  AIProfileResponse,
  ConstraintSimple,
  GetSampleConditionRequest,
  GetSegmentsRequest,
  GetSegmentsResponse,
  QuestionResults,
  SegmentCountResponse,
  SegmentCreateRequest,
  SegmentFavorite,
  SegmentResponse,
  SegmentSimple,
  SegmentSortTerm,
  SegmentUpdateRequest,
} from "types";

import API from "api/client";
import { envConfig } from "config";

const { segmentsAPI } = envConfig;

/**
 * Create a segment
 */
const createSegment = (req: SegmentCreateRequest): Promise<SegmentResponse> => {
  return API.post(`${segmentsAPI}`, req).then((res) => res.data);
};

/**
 * Delete a segment.
 */
const deleteSegment = (uuid: string): Promise<void> => {
  return API.delete(`${segmentsAPI}${uuid}`).then((res) => res.data);
};

/**
 * Get a segment.
 */
const getSegment = (uuid: string): Promise<SegmentResponse> => {
  return API.get(`${segmentsAPI}${uuid}`).then((res) => res.data);
};

/**
 * Favorite a segment.
 */
const favoriteSegment = (uuid: string): Promise<SegmentFavorite> => {
  return API.post(`${segmentsAPI}${uuid}/favorite`).then((res) => res.data);
};

/**
 * Remove a segment favorite.
 */
const unfavoriteSegment = (uuid: string): Promise<void> => {
  return API.delete(`${segmentsAPI}${uuid}/favorite`).then((res) => res.data);
};

/**
 * Converts the single "sortTerm" into `orderBy` and `order` values accepted by the API.
 */
const getSegmentOrdering = (sortTerm: SegmentSortTerm = "created-desc"): { orderBy: string; order: string } => {
  switch (sortTerm) {
    case "name":
      return { orderBy: "name", order: "asc" };
    case "created-asc":
      return { orderBy: "created_time", order: "asc" };
    case "created-desc":
    default:
      sortTerm satisfies "created-desc";
      return { orderBy: "created_time", order: "desc" };
  }
};

/**
 * Get list of segments via search.
 */
const getSegments = (options: GetSegmentsRequest): Promise<GetSegmentsResponse> => {
  const { query, page = 1, size = 10, visibility, favorites } = options;

  return API.get(`${segmentsAPI}`, {
    params: {
      ...getSegmentOrdering(options.sortTerm),
      query,
      page,
      size,
      visibility: visibility ? visibility : undefined,
      favorites,
    },
  }).then((res) => res.data);
};

/**
 * This endpoint fetches all segments available to the given user.
 */
const getAllSegments = (): Promise<SegmentSimple[]> => {
  return API.get(`${segmentsAPI}all`).then((res) => res.data);
};

/**
 * This endpoint fetches all of the given user's favorites.
 */
const getAllSegmentFavorites = (): Promise<SegmentSimple[]> => {
  return API.get(`${segmentsAPI}favorites`).then((res) => res.data);
};

/**
 * Get segment count.
 */
const getSegmentCount = (uuid: string): Promise<SegmentCountResponse> => {
  return API.get(`${segmentsAPI}${uuid}/count`).then((res) => res.data);
};

/**
 * Get segment geo values.
 */
const getGeoValues = (type: string): Promise<ConstraintSimple[]> => {
  return API.get(`${segmentsAPI}geo/${type}`).then((res) => res.data);
};

/**
 * Updates a segment.
 */
const updateSegment = (uuid: string, req: SegmentUpdateRequest): Promise<SegmentResponse> => {
  return API.put(`${segmentsAPI}${uuid}`, req).then((res) => res.data);
};

/**
 * Get segment profile results.
 */
const getSegmentProfileResults = async (uuid: string, pqid: number): Promise<QuestionResults> => {
  return API.get(`${segmentsAPI}${uuid}/profile`, { params: { pqid } }).then((res) => res.data);
};

/**
 * Get an AI-generated summary of a segment's demographic profile.
 */
const getAIProfileSummary = (uuid: string, req: SegmentAIProfileRequest): Promise<AIProfileResponse> => {
  return API.post(`${segmentsAPI}${uuid}/ai-profile`, req).then((res) => res.data);
};

/**
 * Generate draft segment constraints from a free-text prompt.
 */
const getAICondition = (req: SegmentAIConditionRequest): Promise<SegmentAIConditionResponse> => {
  return API.post(`${segmentsAPI}ai-condition`, req).then((res) => res.data);
};

/**
 * Get segment sample condition count.
 */
const getSampleConditionCount = (req: GetSampleConditionRequest): Promise<SegmentCountResponse> => {
  return API.post(`${segmentsAPI}condition/count`, req).then((res) => res.data);
};

export default {
  create: createSegment,
  delete: deleteSegment,
  favorite: favoriteSegment,
  get: getSegment,
  getAICondition: getAICondition,
  getAIProfileSummary: getAIProfileSummary,
  getAll: getAllSegments,
  getCount: getSegmentCount,
  getProfileResults: getSegmentProfileResults,
  getSampleConditionCount: getSampleConditionCount,
  list: getSegments,
  listFavorites: getAllSegmentFavorites,
  listGeoValues: getGeoValues,
  unfavorite: unfavoriteSegment,
  update: updateSegment,
};
